/**
 * Project Types
 *
 * A Project is an optional grouping entity that can contain multiple Jobs.
 * Used for:
 * 1. Multi-trade projects (e.g., kitchen remodel with electrical, plumbing, HVAC)
 * 2. Multi-unit projects (subdivisions, townhomes, apartments, mixed-use developments)
 *
 * Single-trade work can be a Job without a parent Project.
 */

import type { Timestamp } from 'firebase/firestore';

// =============================================================================
// MULTI-UNIT PROJECT TYPES (Phase 9)
// =============================================================================

/**
 * Development project type codes (from Supabase development_project_types table)
 */
export type DevelopmentProjectType =
  | 'townhome'           // Clusters of attached single-family homes
  | 'apartment'          // Multi-family residential development
  | 'subdivision'        // Splitting tract into single-family home lots
  | 'mixed_use'          // Residential + commercial in one development
  | 'commercial_complex' // Multi-building commercial development
  | 'single_property';   // Standard single-property project (not multi-unit)

/**
 * Job scope classification for projects
 * - standalone: Independent job, not part of a project
 * - unit_specific: Job tied to a specific unit (e.g., electrical permit for Lot 12)
 * - shared_common: Shared infrastructure permit (e.g., stormwater for entire project)
 * - campus_site: Site-wide permit (e.g., overall site development permit)
 */
export type JobScopeType =
  | 'standalone'
  | 'unit_specific'
  | 'shared_common'
  | 'campus_site';

/**
 * Project segment types (for grouping units)
 */
export type SegmentType = 'phase' | 'building' | 'block' | 'section' | 'custom';

/**
 * Unit types within a project
 */
export type UnitType =
  | 'lot'       // Subdivision lot
  | 'unit'      // Generic unit
  | 'building'  // Entire building (apartment complex)
  | 'townhome'  // Individual townhome
  | 'apartment' // Individual apartment unit
  | 'space';    // Commercial tenant space

/**
 * Unit status based on permit progress
 */
export type UnitStatus = 'pending' | 'permitting' | 'construction' | 'complete';

/**
 * Segment status based on unit progress
 */
export type SegmentStatus = 'pending' | 'in_progress' | 'complete';

/**
 * Development project status
 */
export type DevelopmentProjectStatus =
  | 'planning'     // Initial planning, pre-permit
  | 'permitting'   // Permits being obtained
  | 'construction' // Active construction
  | 'closeout'     // Final inspections/closeout
  | 'complete';    // Project complete

/**
 * Project segment (optional grouping of units)
 * e.g., "Phase 1", "Building A", "Block 2"
 */
export interface ProjectSegment {
  id: string;
  projectId: string;

  name: string;  // "Phase 1", "Building A", "Block 2"
  segmentType: SegmentType;
  description?: string;

  // Stats (computed from units)
  unitCount: number;
  status: SegmentStatus;

  sortOrder: number;
  createdAt: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

/**
 * Unit within a project
 * e.g., "Lot 12", "Unit 305", "Townhome 14"
 */
export interface Unit {
  id: string;
  projectId: string;
  segmentId?: string;  // Optional grouping

  // Identification
  label: string;  // "Lot 12", "Unit 305", "Building B", "Townhome 14"
  unitType: UnitType;
  description?: string;

  // Address (may differ from project master address)
  address?: {
    street: string;
    unit?: string;
    city: string;
    state: string;
    zip: string;
  };
  parcelId?: string;

  // Building attributes (for replication/templates)
  attributes?: {
    stories?: number;
    sqft?: number;
    bedrooms?: number;
    bathrooms?: number;
    occupancyClass?: string;
    constructionType?: string;
    planSetId?: string;  // Reference to master plan for repeated units
  };

  // Status (computed from jobs)
  status: UnitStatus;
  jobCount: number;
  readyJobs: number;
  blockedJobs: number;

  sortOrder: number;
  createdAt: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

/**
 * Create unit input
 */
export interface CreateUnitInput {
  label: string;
  unitType: UnitType;
  segmentId?: string;
  description?: string;
  address?: Unit['address'];
  parcelId?: string;
  attributes?: Unit['attributes'];
  sortOrder?: number;
}

/**
 * Unit stats for project dashboard
 */
export interface UnitStats {
  total: number;
  ready: number;      // Ready to submit
  blocked: number;    // Has compliance issues
  submitted: number;  // Permits submitted
  issued: number;     // Permits issued
}

/**
 * Shared permit stats for project dashboard
 */
export interface SharedPermitStats {
  total: number;
  ready: number;
  blocked: number;
  submitted: number;
  issued: number;
}

/**
 * Extended Job fields for project linkage (to be added to Job interface)
 */
export interface ProjectJobFields {
  projectId?: string;         // Parent project ID
  unitId?: string;            // Parent unit ID (for unit_specific jobs)
  jobScopeType: JobScopeType; // Classification
  templateJobId?: string;     // Source template for batch-created jobs
  isTemplate?: boolean;       // Is this job a template for replication
}

// =============================================================================
// MULTI-TRADE PROJECT TYPES (Existing)
// =============================================================================

// Project status
export type ProjectStatus =
  | 'draft'          // Initial state, wizard in progress
  | 'active'         // Has active jobs/permits
  | 'submitted'      // All permits submitted
  | 'approved'       // All permits approved/issued
  | 'completed'      // Work finished
  | 'archived';      // Historical record

// Project type category
export type ProjectCategory =
  | 'new_construction'
  | 'renovation'
  | 'repair'
  | 'addition'
  | 'tenant_improvement';

// Property type
export type PropertyType = 'residential' | 'commercial';

// Project address (normalized structure)
export interface ProjectAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
  county?: string;
  formatted?: string;  // Full formatted address string
}

// Project client information
export interface ProjectClient {
  name?: string;
  email?: string;
  phone?: string;
  companyName?: string;
}

// Trade scope within a project
export interface ProjectTradeScope {
  tradeTypeId: number;
  tradeCode: string;      // e.g., 'electrical', 'plumbing'
  tradeName: string;
  jobScopeId?: number;
  jobScopeCode?: string;  // e.g., 'elec_panel_upgrade'
  jobScopeName?: string;
  jobId?: string;         // Reference to created Job document
}

// Completeness tracking
export interface ProjectCompleteness {
  dataComplete: number;    // Percentage 0-100
  packetsComplete: number; // Percentage 0-100
  complianceReady: number; // Percentage 0-100
}

/**
 * Main Project entity
 */
export interface Project {
  id: string;

  // Basic info
  name: string;               // Project name/description (e.g., "Kitchen Remodel")
  category: ProjectCategory;
  propertyType: PropertyType;

  // Location
  address: ProjectAddress;
  jurisdictionId?: number;
  jurisdictionCode?: string;
  jurisdictionName?: string;

  // Client
  client?: ProjectClient;

  // Financial
  estimatedValue?: number;

  // Trade scopes
  tradeScopes: ProjectTradeScope[];

  // Status & Progress
  status: ProjectStatus;
  completeness: ProjectCompleteness;

  // Ownership
  contractorId: string;       // User ID or Org ID
  createdBy: string;          // User who created

  // Timestamps
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;

  // Wizard tracking
  wizardCompleted?: boolean;
  wizardLastStep?: number;

  // Soft delete
  isDeleted?: boolean;
  deletedAt?: Timestamp | Date | null;
}

/**
 * Project creation input (subset for creating new projects)
 */
export interface CreateProjectInput {
  name: string;
  category: ProjectCategory;
  propertyType: PropertyType;
  address: ProjectAddress;
  jurisdictionId?: number;
  client?: ProjectClient;
  estimatedValue?: number;
  tradeScopes?: ProjectTradeScope[];
}

/**
 * Project update input
 */
export interface UpdateProjectInput {
  name?: string;
  category?: ProjectCategory;
  propertyType?: PropertyType;
  address?: ProjectAddress;
  jurisdictionId?: number;
  jurisdictionCode?: string;
  jurisdictionName?: string;
  client?: ProjectClient;
  estimatedValue?: number;
  tradeScopes?: ProjectTradeScope[];
  status?: ProjectStatus;
  completeness?: ProjectCompleteness;
  wizardCompleted?: boolean;
  wizardLastStep?: number;
}

/**
 * Project with computed/joined data
 */
export interface ProjectWithJobs extends Project {
  jobs: Array<{
    id: string;
    title: string;
    status: string;
    permitType?: string;
    complianceStatus?: string;
  }>;
}

// =============================================================================
// DEVELOPMENT PROJECT (MULTI-UNIT)
// =============================================================================

/**
 * Development Project for multi-unit projects
 * e.g., subdivisions, townhome communities, apartment complexes, mixed-use
 *
 * Stored in: /contractors/{contractorId}/projects/{projectId}
 * with developmentType set
 */
export interface DevelopmentProject {
  id: string;

  // Basic info
  name: string;                           // "Oakwood Estates", "Harbor View Apartments"
  developmentType: DevelopmentProjectType; // 'townhome', 'subdivision', etc.
  propertyType: PropertyType;             // 'residential' or 'commercial'
  description?: string;

  // Location
  masterAddress: ProjectAddress;          // Project/development address
  jurisdictionId?: number;
  jurisdictionCode?: string;
  jurisdictionName?: string;

  // Owner/Developer Info
  ownerName?: string;
  ownerCompany?: string;
  ownerPhone?: string;
  ownerEmail?: string;

  // Project Structure
  totalUnits: number;
  segments?: ProjectSegment[];            // Optional grouping

  // Status (computed from units/jobs)
  status: DevelopmentProjectStatus;
  unitStats: UnitStats;
  sharedPermitStats: SharedPermitStats;

  // Ownership
  contractorId: string;
  createdBy: string;

  // Timestamps
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  startedAt?: Timestamp | Date;
  completedAt?: Timestamp | Date;

  // Wizard tracking
  wizardCompleted?: boolean;
  wizardLastStep?: number;

  // Soft delete
  isDeleted?: boolean;
  deletedAt?: Timestamp | Date | null;
}

/**
 * Create development project input
 */
export interface CreateDevelopmentProjectInput {
  name: string;
  developmentType: DevelopmentProjectType;
  propertyType: PropertyType;
  description?: string;
  masterAddress: ProjectAddress;
  jurisdictionId?: number;
  ownerName?: string;
  ownerCompany?: string;
  ownerPhone?: string;
  ownerEmail?: string;
  totalUnits: number;
}

/**
 * Update development project input
 */
export interface UpdateDevelopmentProjectInput {
  name?: string;
  developmentType?: DevelopmentProjectType;
  propertyType?: PropertyType;
  description?: string;
  masterAddress?: ProjectAddress;
  jurisdictionId?: number;
  jurisdictionCode?: string;
  jurisdictionName?: string;
  ownerName?: string;
  ownerCompany?: string;
  ownerPhone?: string;
  ownerEmail?: string;
  totalUnits?: number;
  status?: DevelopmentProjectStatus;
  wizardCompleted?: boolean;
  wizardLastStep?: number;
}

/**
 * Development project with units and jobs
 */
export interface DevelopmentProjectWithUnits extends DevelopmentProject {
  units: Unit[];
  sharedJobs: Array<{
    id: string;
    title: string;
    status: string;
    permitType?: string;
    complianceStatus?: string;
    jobScopeType: JobScopeType;
  }>;
}

/**
 * Batch job creation input (for replicating permits across units)
 */
export interface BatchCreateJobsInput {
  projectId: string;
  unitIds: string[];                     // Units to create jobs for
  templateJobId?: string;                // Copy from existing job
  permitType: string;                    // e.g., 'electrical', 'plumbing'
  tradeType?: string;
  jobScopeType: 'unit_specific' | 'shared_common';
  title?: string;                        // Override title
  description?: string;
}

/**
 * Shared infrastructure permit (for project-wide permits)
 */
export interface SharedPermit {
  id: string;
  projectId: string;
  jobId: string;                         // Reference to the Job document
  permitCategory: string;                // 'stormwater', 'site_development', 'fire_main', etc.
  appliesTo: 'all_units' | 'segment' | 'custom';
  segmentId?: string;                    // If appliesTo === 'segment'
  unitIds?: string[];                    // If appliesTo === 'custom'
  status: 'pending' | 'submitted' | 'approved' | 'rejected';
  createdAt: Timestamp | Date;
}

/**
 * Development process step (from Supabase development_process_steps table)
 */
export interface DevelopmentProcessStep {
  id: string;
  jurisdictionId: number;
  projectTypeCode: DevelopmentProjectType;
  stepNumber: number;
  stepName: string;
  description?: string;
  required: boolean;
  typicalDuration?: string;
  responsibleParty?: string;
  deliverables?: string[];
  agencyReviews?: string[];
  prerequisites?: string[];
  tips?: string[];
  sourceReference?: string;
}

/**
 * Combined project type - can be either multi-trade or development
 */
export type AnyProject = Project | DevelopmentProject;

/**
 * Type guard for DevelopmentProject
 */
export function isDevelopmentProject(project: AnyProject): project is DevelopmentProject {
  return 'developmentType' in project && project.developmentType !== undefined;
}

// =============================================================================
// PROJECT DOCUMENTS
// =============================================================================

/**
 * Document scope within a project
 * - project_wide: Applies to entire project (e.g., master plans, site surveys)
 * - unit_specific: Applies to a specific unit (e.g., unit floor plan)
 * - segment_specific: Applies to a segment/phase (e.g., phase 1 utilities)
 */
export type ProjectDocumentScope = 'project_wide' | 'unit_specific' | 'segment_specific';

/**
 * Project document categories (extends base DocumentCategory)
 */
export type ProjectDocumentCategory =
  | 'site_plan'           // Master site plan
  | 'survey'              // Survey/plat
  | 'civil_engineering'   // Civil engineering plans
  | 'landscape_plan'      // Landscape architecture
  | 'utility_plan'        // Utility coordination (water, sewer, electric)
  | 'stormwater'          // Stormwater management plan
  | 'traffic_study'       // Traffic impact study
  | 'environmental'       // Environmental assessments
  | 'zoning_approval'     // Zoning/variance approvals
  | 'hoa_approval'        // HOA/architectural review
  | 'floor_plan'          // Building floor plans
  | 'elevation'           // Building elevations
  | 'structural'          // Structural plans
  | 'mep'                 // MEP (mechanical/electrical/plumbing)
  | 'permit_drawing'      // Permit set drawings
  | 'specification'       // Specifications
  | 'contract'            // Contracts
  | 'insurance'           // Insurance certificates
  | 'bond'                // Performance/payment bonds
  | 'noc'                 // Notice of Commencement
  | 'inspection_report'   // Inspection reports
  | 'photo'               // Progress photos
  | 'correspondence'      // Official correspondence
  | 'other';

/**
 * Project document (stored in Firestore)
 */
export interface ProjectDocument {
  id: string;
  projectId: string;

  // Scope and targeting
  scope: ProjectDocumentScope;
  unitId?: string;         // If scope === 'unit_specific'
  segmentId?: string;      // If scope === 'segment_specific'

  // Document info
  name: string;
  description?: string;
  category: ProjectDocumentCategory;

  // File info
  fileName: string;
  filePath: string;        // Storage path
  fileSize: number;
  fileType: string;        // MIME type
  downloadUrl?: string;    // Signed URL (may expire)

  // Metadata
  uploadedBy: string;      // User ID
  uploadedByName?: string; // User display name
  tags?: string[];

  // Versioning
  version: number;
  previousVersionId?: string;
  isLatest: boolean;

  // Status
  status: 'active' | 'archived' | 'superseded';

  // Timestamps
  createdAt: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

/**
 * Create project document input
 */
export interface CreateProjectDocumentInput {
  projectId: string;
  scope: ProjectDocumentScope;
  unitId?: string;
  segmentId?: string;
  name: string;
  description?: string;
  category: ProjectDocumentCategory;
  file: File;
  tags?: string[];
}

/**
 * Project document category labels
 */
export const PROJECT_DOCUMENT_CATEGORY_LABELS: Record<ProjectDocumentCategory, string> = {
  site_plan: 'Site Plan',
  survey: 'Survey/Plat',
  civil_engineering: 'Civil Engineering',
  landscape_plan: 'Landscape Plan',
  utility_plan: 'Utility Plan',
  stormwater: 'Stormwater',
  traffic_study: 'Traffic Study',
  environmental: 'Environmental',
  zoning_approval: 'Zoning Approval',
  hoa_approval: 'HOA Approval',
  floor_plan: 'Floor Plan',
  elevation: 'Elevation',
  structural: 'Structural',
  mep: 'MEP',
  permit_drawing: 'Permit Drawing',
  specification: 'Specification',
  contract: 'Contract',
  insurance: 'Insurance',
  bond: 'Bond',
  noc: 'Notice of Commencement',
  inspection_report: 'Inspection Report',
  photo: 'Photo',
  correspondence: 'Correspondence',
  other: 'Other',
};

// =============================================================================
// PROJECT MILESTONES
// =============================================================================

/**
 * Milestone status
 */
export type MilestoneStatus = 'pending' | 'in_progress' | 'complete' | 'delayed' | 'blocked';

/**
 * Project milestone
 */
export interface ProjectMilestone {
  id: string;
  projectId: string;

  // Milestone info
  name: string;
  description?: string;
  category: 'permitting' | 'construction' | 'inspection' | 'administrative' | 'other';

  // Target and completion dates
  targetDate?: Timestamp | Date;
  completedDate?: Timestamp | Date;

  // Status
  status: MilestoneStatus;

  // Dependencies
  dependsOn?: string[];    // IDs of other milestones
  blockedBy?: string[];    // IDs of blocking items (jobs, units)

  // Scope
  scope: 'project' | 'segment' | 'unit';
  segmentId?: string;
  unitId?: string;

  // Tracking
  sortOrder: number;
  createdAt: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}
